import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGroup } from './api';
import type { IAssemblyGroup } from './types';

const STORAGE_KEY = 'cpq_group_key';

export const useAssemblyGroup = () => {
  const [groupKey, setGroupKey] = useState<string | null>(
    localStorage.getItem(STORAGE_KEY),
  );

  const { data: group, refetch, error } = useQuery<IAssemblyGroup>({
    queryKey: ['assembly-group', groupKey],
    queryFn: () => getGroup(groupKey!),
    enabled: !!groupKey,
    retry: false,
  });

  // Auto-clear ONLY expired groups.
  // Do NOT clear on 'completed' — Email/Open PDF buttons depend on groupKey
  // for getFreshPdfUrl(). Clearing on completed causes a race condition:
  // refetchOnWindowFocus → useEffect → groupKey=null → getFreshPdfUrl fails.
  // Instead, handleAddToGroup checks if current group is completed and creates new one.
  useEffect(() => {
    if (!group) return;
    if (group.status === 'expired') {
      localStorage.removeItem(STORAGE_KEY);
      setGroupKey(null);
    }
  }, [group]);

  // Handle 404 (group deleted by cron before status update)
  useEffect(() => {
    const errStatus = error && typeof error === 'object' && 'status' in error
      ? (error as { status: number }).status
      : undefined;
    if (errStatus === 404) {
      localStorage.removeItem(STORAGE_KEY);
      setGroupKey(null);
    }
  }, [error]);

  const saveGroupKey = useCallback((key: string) => {
    localStorage.setItem(STORAGE_KEY, key);
    setGroupKey(key);
  }, []);

  const clearGroupKey = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setGroupKey(null);
  }, []);

  return {
    groupKey,
    group,
    setGroupKey: saveGroupKey,
    clearGroupKey,
    refetch,
  };
};
